import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"


// =========================================================
// CONSTANTS
// =========================================================

const BRAND = {
  name:
    "RLSys Business",

  subtitle:
    "Retail & Logistics System",

  navy:
    [15, 23, 42],

  blue:
    [37, 99, 235],

  lightBlue:
    [239, 246, 255],

  lightGray:
    [248, 250, 252],

  border:
    [226, 232, 240],

  text:
    [15, 23, 42],

  muted:
    [100, 116, 139],
}


// =========================================================
// GENERIC HELPERS
// =========================================================

const safeText = (
  value
) => {
  if (
    value === null ||
    value === undefined
  ) {
    return ""
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(
      value
    )
  }

  return ""
}


const slugify = (
  value
) =>
  safeText(
    value
  )
    .normalize(
      "NFD"
    )
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .replace(
      /[^a-zA-Z0-9]+/g,
      "_"
    )
    .replace(
      /^_+|_+$/g,
      ""
    )
    .toLowerCase()


const formatGeneratedDate = (
  value
) => {
  const date =
    value instanceof Date
      ? value
      : new Date(
          value
        )

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return ""
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle:
        "medium",

      timeStyle:
        "short",
    }
  )
}


const normalizeCellValue = (
  column,
  row
) => {
  try {
    if (
      typeof column.exportValue ===
      "function"
    ) {
      return safeText(
        column.exportValue(
          row
        )
      )
    }

    if (
      typeof column.render ===
      "function"
    ) {
      const rendered =
        column.render(
          row
        )

      const normalized =
        safeText(
          rendered
        )

      if (
        normalized
      ) {
        return normalized
      }
    }
  } catch (
    error
  ) {
    console.warn(
      "RLSys export formatter:",
      error
    )
  }

  return safeText(
    row?.[
      column.key
    ]
  )
}


const getExportTable = (
  columns = [],
  rows = []
) => {
  const headers =
    columns.map(
      (
        column
      ) =>
        safeText(
          column.label
        )
    )

  const body =
    rows.map(
      (
        row
      ) =>
        columns.map(
          (
            column
          ) =>
            normalizeCellValue(
              column,
              row
            )
        )
    )

  return {
    headers,
    body,
  }
}


const buildFileBaseName = (
  payload
) => {
  const report =
    slugify(
      payload?.reportTitle ||
        "rapport"
    )

  const date =
    new Date()
      .toISOString()
      .slice(
        0,
        10
      )

  return `RLSys_${report}_${date}`
}


const getUsefulContext = (
  context = {}
) =>
  Object.entries(
    context
  ).filter(
    ([
      ,
      value,
    ]) =>
      value !==
        "Non applicable" &&
      value !==
        "" &&
      value !==
        null &&
      value !==
        undefined
  )


// =========================================================
// CSV
// =========================================================

const csvEscape = (
  value
) => {
  const text =
    safeText(
      value
    )

  if (
    text.includes(
      ","
    ) ||
    text.includes(
      '"'
    ) ||
    text.includes(
      "\n"
    )
  ) {
    return `"${text.replace(
      /"/g,
      '""'
    )}"`
  }

  return text
}


export const exportReportToCsv =
  (
    payload
  ) => {
    const {
      headers,
      body,
    } =
      getExportTable(
        payload?.columns,
        payload?.rows
      )

    if (
      !headers.length ||
      !body.length
    ) {
      return
    }

    const content =
      [
        headers,
        ...body,
      ]
        .map(
          (
            row
          ) =>
            row
              .map(
                csvEscape
              )
              .join(
                ","
              )
        )
        .join(
          "\n"
        )

    const blob =
      new Blob(
        [
          "\ufeff",
          content,
        ],
        {
          type:
            "text/csv;charset=utf-8;",
        }
      )

    const url =
      URL.createObjectURL(
        blob
      )

    const link =
      document.createElement(
        "a"
      )

    link.href =
      url

    link.download =
      `${buildFileBaseName(
        payload
      )}.csv`

    document.body.appendChild(
      link
    )

    link.click()

    link.remove()

    URL.revokeObjectURL(
      url
    )
  }


// =========================================================
// EXCEL
// =========================================================

export const exportReportToExcel =
  (
    payload
  ) => {
    const workbook =
      XLSX.utils.book_new()

    const sheetRows =
      []

    sheetRows.push([
      payload?.appName ||
        BRAND.name,
    ])

    sheetRows.push([
      payload?.appSubtitle ||
        BRAND.subtitle,
    ])

    sheetRows.push([])

    sheetRows.push([
      payload?.reportTitle ||
        "Rapport",
    ])

    if (
      payload?.reportDescription
    ) {
      sheetRows.push([
        payload.reportDescription,
      ])
    }

    sheetRows.push([])

    sheetRows.push([
      "Catégorie",
      payload?.reportGroup ||
        "",
    ])

    sheetRows.push([
      "Période",
      payload?.periodLabel ||
        "",
    ])

    sheetRows.push([
      "Généré le",
      formatGeneratedDate(
        payload?.generatedAt
      ),
    ])

    sheetRows.push([
      "Généré par",
      payload?.generatedBy ||
        "",
    ])

    const usefulContext =
      getUsefulContext(
        payload?.context
      )

    usefulContext.forEach(
      ([
        key,
        value,
      ]) => {
        sheetRows.push([
          key,
          safeText(
            value
          ),
        ])
      }
    )

    const cards =
      Array.isArray(
        payload?.cards
      )
        ? payload.cards
        : []

    if (
      cards.length
    ) {
      sheetRows.push([])
      sheetRows.push([
        "INDICATEURS CLÉS",
      ])

      cards.forEach(
        (
          card
        ) => {
          sheetRows.push([
            card.label,
            card.value,
          ])
        }
      )
    }

    const {
      headers,
      body,
    } =
      getExportTable(
        payload?.columns,
        payload?.rows
      )

    if (
      headers.length
    ) {
      sheetRows.push([])
      sheetRows.push([
        "DÉTAIL DU RAPPORT",
      ])
      sheetRows.push(
        headers
      )
      sheetRows.push(
        ...body
      )
    }

    const worksheet =
      XLSX.utils.aoa_to_sheet(
        sheetRows
      )

    const maxColumns =
      Math.max(
        headers.length,
        2
      )

    worksheet["!cols"] =
      Array.from(
        {
          length:
            maxColumns,
        },
        (
          _,
          index
        ) => ({
          wch:
            index === 0
              ? 26
              : 20,
        })
      )

    worksheet["!freeze"] = {
      xSplit:
        0,
      ySplit:
        0,
    }

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Rapport"
    )

    XLSX.writeFile(
      workbook,
      `${buildFileBaseName(
        payload
      )}.xlsx`
    )
  }


// =========================================================
// PDF
// =========================================================

const drawPdfHeader = (
  doc,
  payload,
  pageWidth
) => {
  doc.setFillColor(
    ...BRAND.navy
  )

  doc.rect(
    0,
    0,
    pageWidth,
    26,
    "F"
  )

  doc.setTextColor(
    255,
    255,
    255
  )

  doc.setFont(
    "helvetica",
    "bold"
  )

  doc.setFontSize(
    15
  )

  doc.text(
    payload?.appName ||
      BRAND.name,
    14,
    10
  )

  doc.setFont(
    "helvetica",
    "normal"
  )

  doc.setFontSize(
    8.5
  )

  doc.text(
    payload?.appSubtitle ||
      BRAND.subtitle,
    14,
    16
  )

  doc.setFont(
    "helvetica",
    "bold"
  )

  doc.setFontSize(
    9
  )

  doc.text(
    "CENTRE DE RAPPORTS",
    pageWidth - 14,
    10,
    {
      align:
        "right",
    }
  )

  doc.setFont(
    "helvetica",
    "normal"
  )

  doc.setFontSize(
    7.5
  )

  doc.text(
    formatGeneratedDate(
      payload?.generatedAt
    ),
    pageWidth - 14,
    16,
    {
      align:
        "right",
    }
  )
}


const drawPdfFooter = (
  doc,
  pageWidth,
  pageHeight
) => {
  const pageCount =
    doc.internal.getNumberOfPages()

  for (
    let pageNumber = 1;
    pageNumber <= pageCount;
    pageNumber += 1
  ) {
    doc.setPage(
      pageNumber
    )

    doc.setDrawColor(
      ...BRAND.border
    )

    doc.line(
      14,
      pageHeight - 12,
      pageWidth - 14,
      pageHeight - 12
    )

    doc.setTextColor(
      ...BRAND.muted
    )

    doc.setFontSize(
      7.5
    )

    doc.text(
      "RLSys Business • Rapport généré automatiquement",
      14,
      pageHeight - 7
    )

    doc.text(
      `Page ${pageNumber} / ${pageCount}`,
      pageWidth - 14,
      pageHeight - 7,
      {
        align:
          "right",
      }
    )
  }
}


export const exportReportToPdf =
  (
    payload
  ) => {
    const {
      headers,
      body,
    } =
      getExportTable(
        payload?.columns,
        payload?.rows
      )

    const landscape =
      headers.length > 6

    const doc =
      new jsPDF({
        orientation:
          landscape
            ? "landscape"
            : "portrait",

        unit:
          "mm",

        format:
          "a4",
      })

    const pageWidth =
      doc.internal.pageSize.getWidth()

    const pageHeight =
      doc.internal.pageSize.getHeight()

    drawPdfHeader(
      doc,
      payload,
      pageWidth
    )

    let cursorY =
      35

    doc.setTextColor(
      ...BRAND.text
    )

    doc.setFont(
      "helvetica",
      "bold"
    )

    doc.setFontSize(
      17
    )

    doc.text(
      payload?.reportTitle ||
        "Rapport",
      14,
      cursorY
    )

    cursorY +=
      6

    if (
      payload?.reportDescription
    ) {
      doc.setFont(
        "helvetica",
        "normal"
      )

      doc.setFontSize(
        8.5
      )

      doc.setTextColor(
        ...BRAND.muted
      )

      const descriptionLines =
        doc.splitTextToSize(
          payload.reportDescription,
          pageWidth - 28
        )

      doc.text(
        descriptionLines,
        14,
        cursorY
      )

      cursorY +=
        descriptionLines.length *
          4 +
        2
    }

    doc.setFillColor(
      ...BRAND.lightGray
    )

    doc.roundedRect(
      14,
      cursorY,
      pageWidth - 28,
      20,
      2,
      2,
      "F"
    )

    doc.setFont(
      "helvetica",
      "bold"
    )

    doc.setFontSize(
      8
    )

    doc.setTextColor(
      ...BRAND.text
    )

    doc.text(
      `Catégorie : ${payload?.reportGroup || "-"}`,
      18,
      cursorY + 6
    )

    doc.text(
      `Période : ${payload?.periodLabel || "-"}`,
      18,
      cursorY + 12
    )

    doc.setFont(
      "helvetica",
      "normal"
    )

    doc.text(
      `Généré par : ${payload?.generatedBy || "-"}`,
      pageWidth / 2,
      cursorY + 6
    )

    const contextLines =
      getUsefulContext(
        payload?.context
      )
        .slice(
          0,
          3
        )
        .map(
          ([
            key,
            value,
          ]) =>
            `${key} : ${safeText(
              value
            )}`
        )

    if (
      contextLines.length
    ) {
      doc.text(
        contextLines.join(
          "  •  "
        ),
        pageWidth / 2,
        cursorY + 12
      )
    }

    cursorY +=
      28

    const cards =
      Array.isArray(
        payload?.cards
      )
        ? payload.cards
        : []

    if (
      cards.length
    ) {
      doc.setFont(
        "helvetica",
        "bold"
      )

      doc.setFontSize(
        10
      )

      doc.setTextColor(
        ...BRAND.text
      )

      doc.text(
        "Indicateurs clés",
        14,
        cursorY
      )

      cursorY +=
        5

      const availableWidth =
        pageWidth - 28

      const columns =
        landscape
          ? Math.min(
              4,
              cards.length
            )
          : Math.min(
              3,
              cards.length
            )

      const gap =
        4

      const cardWidth =
        (
          availableWidth -
          gap *
            (
              columns -
              1
            )
        ) /
        columns

      cards.forEach(
        (
          card,
          index
        ) => {
          const column =
            index %
            columns

          const row =
            Math.floor(
              index /
                columns
            )

          const x =
            14 +
            column *
              (
                cardWidth +
                gap
              )

          const y =
            cursorY +
            row *
              22

          doc.setFillColor(
            ...BRAND.lightBlue
          )

          doc.roundedRect(
            x,
            y,
            cardWidth,
            18,
            2,
            2,
            "F"
          )

          doc.setTextColor(
            ...BRAND.muted
          )

          doc.setFont(
            "helvetica",
            "normal"
          )

          doc.setFontSize(
            7.5
          )

          doc.text(
            safeText(
              card.label
            ),
            x + 3,
            y + 5
          )

          doc.setTextColor(
            ...BRAND.text
          )

          doc.setFont(
            "helvetica",
            "bold"
          )

          doc.setFontSize(
            11
          )

          doc.text(
            safeText(
              card.value
            ),
            x + 3,
            y + 12
          )
        }
      )

      cursorY +=
        Math.ceil(
          cards.length /
            columns
        ) *
          22 +
        4
    }

    if (
      headers.length &&
      body.length
    ) {
      doc.setFont(
        "helvetica",
        "bold"
      )

      doc.setFontSize(
        10
      )

      doc.setTextColor(
        ...BRAND.text
      )

      doc.text(
        "Détail du rapport",
        14,
        cursorY
      )

      cursorY +=
        3

      autoTable(
        doc,
        {
          startY:
            cursorY,

          head: [
            headers,
          ],

          body,

          margin: {
            left:
              14,

            right:
              14,

            top:
              31,

            bottom:
              17,
          },

          theme:
            "grid",

          styles: {
            fontSize:
              landscape
                ? 7
                : 7.5,

            cellPadding:
              2.2,

            textColor:
              BRAND.text,

            lineColor:
              BRAND.border,

            lineWidth:
              0.15,

            overflow:
              "linebreak",
          },

          headStyles: {
            fillColor:
              BRAND.navy,

            textColor:
              [255, 255, 255],

            fontStyle:
              "bold",

            halign:
              "left",
          },

          alternateRowStyles: {
            fillColor:
              BRAND.lightGray,
          },

          didDrawPage:
            () => {
              drawPdfHeader(
                doc,
                payload,
                pageWidth
              )
            },
        }
      )
    } else if (
      cards.length
    ) {
      doc.setFillColor(
        ...BRAND.lightGray
      )

      doc.roundedRect(
        14,
        cursorY,
        pageWidth - 28,
        24,
        2,
        2,
        "F"
      )

      doc.setTextColor(
        ...BRAND.muted
      )

      doc.setFont(
        "helvetica",
        "normal"
      )

      doc.setFontSize(
        8.5
      )

      doc.text(
        "Ce rapport est une synthèse. Les indicateurs ci-dessus constituent le résultat consolidé.",
        18,
        cursorY + 10
      )

      doc.text(
        "Aucun tableau détaillé supplémentaire n'est requis pour cette vue.",
        18,
        cursorY + 16
      )
    }

    drawPdfFooter(
      doc,
      pageWidth,
      pageHeight
    )

    doc.save(
      `${buildFileBaseName(
        payload
      )}.pdf`
    )
  }
